import { useEffect, useRef, useState } from 'react';
import { apiPost, operationsClient } from '@/lib/api';
import { schedulerEndpoints } from '@/lib/api/endpoints';
import { useBusinessSettings } from '@/hooks/settings/useBusinessSettings';

export type SharingStatus =
  | 'off' // not enabled (no active appointment / paused / not a tech)
  | 'sharing' // a fix has been posted
  | 'acquiring' // watching, no fix yet
  | 'denied' // the browser denied the geolocation permission
  | 'unavailable'; // geolocation not supported

/** Ping cadence fallback when BusinessSettings hasn't loaded (matches the
 *  gpsPingIntervalSeconds default). */
const DEFAULT_PING_INTERVAL_SECONDS = 60;

/**
 * §9 tech-side GPS emitter. When `enabled`, watches the browser's high-accuracy
 * position and POSTs `/tracking/ping` at the tenant's configured cadence
 * (BusinessSettings.gpsPingIntervalSeconds, default 60s). The backend gate
 * (tech role · active dispatched appt · working hours · not paused) is the
 * source of truth — a gated 4xx is swallowed silently (expected, no toast).
 * The watch stops on disable / unmount / permission denial. Tab-open only: no
 * background polling, and the watch is cleared the moment `enabled` goes false.
 */
export function useTechLocationSharing({ enabled }: { enabled: boolean }): {
  status: SharingStatus;
} {
  const [status, setStatus] = useState<SharingStatus>('off');
  const lastPostRef = useRef(0);

  // Honour the tenant's configurable GPS cadence (BusinessSettings.
  // gpsPingIntervalSeconds). Kept in a ref so a settings change updates the
  // throttle without restarting the geolocation watch. Defaults to 60s while the
  // query is loading (or for a tech without company:view — silently falls back).
  const { data: settings } = useBusinessSettings();
  const throttleMsRef = useRef(DEFAULT_PING_INTERVAL_SECONDS * 1000);
  throttleMsRef.current =
    (settings?.gpsPingIntervalSeconds ?? DEFAULT_PING_INTERVAL_SECONDS) * 1000;

  useEffect(() => {
    if (!enabled) {
      setStatus('off');
      return;
    }
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      setStatus('unavailable');
      return;
    }

    let cancelled = false;
    setStatus('acquiring');
    lastPostRef.current = 0; // post the first fix immediately

    const post = async (pos: GeolocationPosition) => {
      const now = Date.now();
      if (now - lastPostRef.current < throttleMsRef.current) return;
      lastPostRef.current = now;
      try {
        await apiPost(operationsClient, schedulerEndpoints.trackingPing(), {
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
          accuracy: typeof pos.coords.accuracy === 'number' ? pos.coords.accuracy : undefined,
          at: new Date(pos.timestamp).toISOString(),
        });
        if (!cancelled) setStatus('sharing');
      } catch {
        // Gated (paused / outside hours / no active appt) or transient — stay
        // quiet (the gate is expected) and let the next throttled fix retry.
      }
    };

    const watchId = navigator.geolocation.watchPosition(
      (pos) => {
        if (!cancelled) void post(pos);
      },
      (err) => {
        if (cancelled) return;
        setStatus(err.code === err.PERMISSION_DENIED ? 'denied' : 'acquiring');
      },
      { enableHighAccuracy: true, maximumAge: 30_000, timeout: 20_000 },
    );

    return () => {
      cancelled = true;
      navigator.geolocation.clearWatch(watchId);
    };
  }, [enabled]);

  return { status };
}
